import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete';
import { type ContentObjectOption, ContentObjectOptions } from '@openx/types/targeting/contentObject';
import omit from 'lodash/omit';
import { type JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useTargetingOptionDisabledState } from '../../../../utils/hooks/useTargetingOptionDisabledState';
import { getOptionName } from '../../constants';

type TypeOptionDropdownProps = {
  enabledTypes: string[];
  onChange: (type: ContentObjectOption) => void;
  value: ContentObjectOption;
};

export const TypeOptionDropdown = ({ enabledTypes, value, onChange }: TypeOptionDropdownProps): JSX.Element => {
  const { t } = useTranslation();
  const { isOptionDisabled } = useTargetingOptionDisabledState();

  const optionsOrderByDesign = useMemo(
    () => [
      ContentObjectOptions.GENRE,
      ContentObjectOptions.CONTENT_RATING,
      ContentObjectOptions.LIVESTREAM,
      ContentObjectOptions.LENGTH,
      ContentObjectOptions.LANGUAGE,
      ContentObjectOptions.SERIES,
      ContentObjectOptions.EPISODE,
      ContentObjectOptions.CHANNEL,
      ContentObjectOptions.NETWORK,
    ],
    []
  );

  const dropdownOptions = useMemo(
    () =>
      Object.values(omit(ContentObjectOptions, 'CONTENT_INTER_DIMENSION_OPERATOR'))
        .filter(option => enabledTypes.includes(option))
        .sort((a, b) => optionsOrderByDesign.indexOf(a) - optionsOrderByDesign.indexOf(b)),
    [enabledTypes, optionsOrderByDesign]
  );

  return (
    <AutoComplete<ContentObjectOption, false, true, false>
      textFieldProps={{ label: t('Type') }}
      options={dropdownOptions}
      renderOptions={{
        dataTest: 'type-option',
      }}
      getOptionLabel={option => getOptionName(option)}
      getOptionDisabled={option => isOptionDisabled(option)}
      textFieldReadOnly
      disableClearable
      filterOptions={option => option}
      onChange={(_, value) => onChange(value)}
      value={value}
      fullWidth
      data-test="content-object-type"
    />
  );
};
